import type { ReactNode } from 'react';
import { Button } from '../atoms/Button';
import { FormField } from '../molecules/FormField';
import { DatePicker } from '../molecules/DatePicker';
import { FacetButton, FacetList, type FacetOption } from '../molecules/FacetButton';
import { Icon } from '../atoms/Icon';
import { FilterChips, type FilterChipsProps } from '../molecules/FilterChips';
import { Menu } from '../molecules/Menu';
import { compactQuery, phoneQuery, useMediaQuery } from '../hooks/useMediaQuery';
import { Popover } from '../molecules/Popover';
import { SearchField } from '../molecules/SearchField';
import { SegmentedControl, type SegmentedOption } from '../molecules/SegmentedControl';
import styles from './ListToolbar.module.css';

export type ListFacet =
  | {
      type: 'select';
      key: string;
      label: string;
      value: string;
      /** The value that means "not filtered"; the facet is highlighted otherwise. */
      neutral?: string;
      options: FacetOption[];
      onChange: (value: string) => void;
    }
  | {
      type: 'toggle';
      key: string;
      label: string;
      active: boolean;
      onChange: (active: boolean) => void;
    }
  | {
      type: 'dates';
      key: string;
      label: string;
      from: string;
      to: string;
      fromLabel: string;
      toLabel: string;
      /** e.g. "01.01.2026 – 31.01.2026"; empty when no date is set. */
      valueLabel: string;
      presets?: { label: string; from: string; to: string }[];
      onChange: (range: { from: string; to: string }) => void;
    };

export interface ListToolbarProps {
  search?: {
    value: string;
    onChange: (value: string) => void;
    /** Placeholder and accessible name, e.g. "Suchen". */
    label: string;
    clearLabel: string;
  };
  /** Up to three scopes as a segmented control, e.g. Aktiv · Archiv · Alle. */
  scope?: {
    label: string;
    value: string;
    options: SegmentedOption<string>[];
    onChange: (value: string) => void;
  };
  /** Further filters in a popover; changes apply immediately. */
  filters?: {
    /** Button label, e.g. "Filter". */
    label: string;
    activeCount: number;
    content: ReactNode;
  };
  /**
   * Desktop and tablet (#113): every facet is its own button in the toolbar
   * and opens its values directly; replaces `filters` and `chips` there.
   * Phones keep `filters` and `chips`.
   */
  facets?: {
    items: ListFacet[];
    /** e.g. `(name, value) => \`${name}: ${value}, Filter entfernen\``. */
    clearLabel: (name: string, value: string) => string;
    /** Placeholder of the search field in long value lists. */
    searchLabel: string;
    /** "Zurücksetzen", shown while any facet is active. */
    resetLabel: string;
    onReset: () => void;
  };
  /** Sort menu. Tables also sort via column headers; set `phoneOnly` then. */
  sort?: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
    phoneOnly?: boolean;
  };
  /** Active filters as removable chips below the toolbar. */
  chips?: FilterChipsProps;
  /** Replaces the toolbar while rows are selected (bulk actions). */
  selection?: {
    /** e.g. "3 ausgewählt". */
    label: string;
    actions: ReactNode;
    clearLabel: string;
    onClear: () => void;
  };
  /** Rarely: further compact actions on the right, e.g. export. */
  actions?: ReactNode;
}

/**
 * The one toolbar for every list (docs/ux-richtlinien.md §6.2–6.5): search,
 * scope, filters, sorting, active filter chips and bulk actions.
 *
 * Phones (#60): one row. The scope becomes a compact menu button showing the
 * current choice, filters and sorting become icon buttons (the filter button
 * carries the number of active filters).
 */
export function ListToolbar({
  search,
  scope,
  filters,
  facets,
  sort,
  chips,
  selection,
  actions,
}: ListToolbarProps) {
  const phone = useMediaQuery(phoneQuery);
  const compact = useMediaQuery(compactQuery);
  // A toolbar with nothing but the phones' sort menu (tables sort by their
  // headers elsewhere) takes no room on wider screens.
  const onlyPhoneSort =
    sort?.phoneOnly && !search && !scope && !filters && !facets && !chips && !actions;
  if (onlyPhoneSort && !compact && !selection) return null;
  if (selection) {
    return (
      <div className={styles.toolbar} role="toolbar" aria-label={selection.label}>
        <div className={styles.selection}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={selection.clearLabel}
            onClick={selection.onClear}
            iconStart={<Icon name="close" size="sm" />}
          />
          <span className={styles.count}>{selection.label}</span>
        </div>
        <div className={styles.bulk}>{selection.actions}</div>
      </div>
    );
  }
  const sortLabel = sort?.options.find((o) => o.value === sort.value)?.label;
  if (phone) {
    const scopeLabel = scope?.options.find((o) => o.value === scope.value)?.label;
    const filterLabel =
      filters && filters.activeCount > 0
        ? `${filters.label} (${filters.activeCount})`
        : filters?.label;
    return (
      <div className={styles.wrapper}>
        <div className={`${styles.toolbar} ${styles.phone}`}>
          {search && (
            <div className={styles.search}>
              <SearchField
                value={search.value}
                onValueChange={search.onChange}
                placeholder={search.label}
                aria-label={search.label}
                clearLabel={search.clearLabel}
              />
            </div>
          )}
          {scope && (
            <Menu
              align="start"
              trigger={(p) => (
                <Button
                  {...p}
                  variant="secondary"
                  aria-label={`${scope.label}: ${scopeLabel ?? ''}`}
                  iconEnd={<Icon name="chevron-down" size="sm" />}
                >
                  {scopeLabel}
                </Button>
              )}
              items={scope.options.map((o) => ({
                key: o.value,
                label: o.label,
                checked: o.value === scope.value,
                onSelect: () => scope.onChange(o.value),
              }))}
            />
          )}
          {filters && (
            <Popover
              label={filters.label}
              align="end"
              trigger={(p) => (
                <span className={styles.badged}>
                  <Button
                    {...p}
                    variant={filters.activeCount > 0 ? 'soft' : 'secondary'}
                    aria-label={filterLabel}
                    iconStart={<Icon name="filter" size="md" />}
                  />
                  {filters.activeCount > 0 && (
                    <span className={styles.badge} aria-hidden="true">
                      {filters.activeCount}
                    </span>
                  )}
                </span>
              )}
            >
              {filters.content}
            </Popover>
          )}
          {sort && (
            <Menu
              align="end"
              trigger={(p) => (
                <Button
                  {...p}
                  variant="secondary"
                  aria-label={`${sort.label}: ${sortLabel ?? ''}`}
                  iconStart={<Icon name="chevrons-up-down" size="md" />}
                />
              )}
              items={sort.options.map((o) => ({
                key: o.value,
                label: o.label,
                checked: o.value === sort.value,
                onSelect: () => sort.onChange(o.value),
              }))}
            />
          )}
          {actions}
        </div>
        {chips && <FilterChips {...chips} />}
      </div>
    );
  }
  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        {search && (
          <div className={styles.search}>
            <SearchField
              value={search.value}
              onValueChange={search.onChange}
              placeholder={search.label}
              aria-label={search.label}
              clearLabel={search.clearLabel}
              size="sm"
            />
          </div>
        )}
        <div className={styles.controls}>
          {scope && (
            <SegmentedControl
              label={scope.label}
              size="sm"
              value={scope.value}
              onChange={scope.onChange}
              options={scope.options}
            />
          )}
          {facets && <Facets {...facets} />}
          {!facets && filters && (
            <Popover
              label={filters.label}
              trigger={(p) => (
                <Button
                  {...p}
                  variant={filters.activeCount > 0 ? 'soft' : 'secondary'}
                  size="sm"
                  iconStart={<Icon name="filter" size="sm" />}
                >
                  {filters.activeCount > 0
                    ? `${filters.label} (${filters.activeCount})`
                    : filters.label}
                </Button>
              )}
            >
              {filters.content}
            </Popover>
          )}
          {sort && (
            <div className={sort.phoneOnly ? styles.phoneOnly : undefined}>
              <Menu
                align="end"
                trigger={(p) => (
                  <Button
                    {...p}
                    variant="secondary"
                    size="sm"
                    aria-label={`${sort.label}: ${sortLabel ?? ''}`}
                    iconStart={<Icon name="chevrons-up-down" size="sm" />}
                  >
                    {sortLabel}
                  </Button>
                )}
                items={sort.options.map((o) => ({
                  key: o.value,
                  label: o.label,
                  checked: o.value === sort.value,
                  onSelect: () => sort.onChange(o.value),
                }))}
              />
            </div>
          )}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      </div>
      {chips && !facets && <FilterChips {...chips} />}
    </div>
  );
}

function isActive(f: ListFacet): boolean {
  if (f.type === 'toggle') return f.active;
  if (f.type === 'dates') return f.from !== '' || f.to !== '';
  return f.value !== (f.neutral ?? '');
}

function Facets({
  items,
  clearLabel,
  searchLabel,
  resetLabel,
  onReset,
}: NonNullable<ListToolbarProps['facets']>) {
  return (
    <>
      {items.map((f) => {
        if (f.type === 'toggle') {
          return (
            <Button
              key={f.key}
              size="sm"
              variant={f.active ? 'soft' : 'secondary'}
              aria-pressed={f.active}
              onClick={() => f.onChange(!f.active)}
            >
              {f.label}
            </Button>
          );
        }
        if (f.type === 'dates') {
          return (
            <FacetButton
              key={f.key}
              label={f.label}
              valueLabel={f.valueLabel}
              active={isActive(f)}
              clearLabel={clearLabel(f.label, f.valueLabel)}
              onClear={() => f.onChange({ from: '', to: '' })}
            >
              {(close) => (
                <div className={styles.dates}>
                  {f.presets && (
                    <div className={styles.presets}>
                      {f.presets.map((p) => (
                        <Button
                          key={p.label}
                          size="xs"
                          variant="secondary"
                          onClick={() => {
                            f.onChange({ from: p.from, to: p.to });
                            close();
                          }}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  <FormField label={f.fromLabel}>
                    {(p) => (
                      <DatePicker
                        {...p}
                        value={f.from}
                        onChange={(v) => f.onChange({ from: v, to: f.to })}
                      />
                    )}
                  </FormField>
                  <FormField label={f.toLabel}>
                    {(p) => (
                      <DatePicker
                        {...p}
                        value={f.to}
                        onChange={(v) => f.onChange({ from: f.from, to: v })}
                      />
                    )}
                  </FormField>
                </div>
              )}
            </FacetButton>
          );
        }
        const current = f.options.find((o) => o.value === f.value)?.label;
        return (
          <FacetButton
            key={f.key}
            label={f.label}
            {...(current ? { valueLabel: current } : {})}
            active={isActive(f)}
            clearLabel={clearLabel(f.label, current ?? '')}
            onClear={() => f.onChange(f.neutral ?? '')}
          >
            {(close) => (
              <FacetList
                label={f.label}
                options={f.options}
                value={f.value}
                searchLabel={searchLabel}
                onSelect={(v) => {
                  f.onChange(v);
                  close();
                }}
              />
            )}
          </FacetButton>
        );
      })}
      {items.some(isActive) && (
        <Button variant="plain" size="sm" onClick={onReset}>
          {resetLabel}
        </Button>
      )}
    </>
  );
}
